/**
 * /publisher/releases/:releaseId — one release, after it has been assembled.
 *
 * §22–§26: the metadata, the status, the per-platform delivery state, the event history,
 * and the failure explanation when there is one. §28–§30: everything on this page is read
 * from the persisted record rather than from wizard state, so a reload shows the same
 * release with the same status and the same history.
 *
 * ============================================================================
 * EVERY PROVIDER FACT ON THIS PAGE IS EITHER A REAL PROVIDER VALUE OR VISIBLY ABSENT.
 *
 * The four fields a distribution page normally shows with confidence — UPC, ISRC,
 * per-store status, and "live on Spotify" — are exactly the four §23–§25 forbid
 * inventing. So:
 *   - UPC and ISRC render as "Not assigned yet", because LabelGrid assigns both
 *     (`barcode_number` auto-generates; `isrc` is issued) and neither may be computed
 *     locally.
 *   - The platform list is refreshed through `publisherApi.getReleasePlatforms`, but
 *     explicit provider-shaped rows already stored in mock/demo state are still rendered
 *     as such. With no provider response and no stored rows, the page says so rather than
 *     inventing a grid of stores with hopeful spinners.
 *   - Where a provider status DOES exist, its copy is decided by
 *     `labelgrid.describeDeliveryClaim`, whose strongest wording is "delivered to" —
 *     LabelGrid confirms delivery, and no LabelGrid field says "live in the store".
 *   - Timestamps are printed through lib/publisherFormat, which returns an em dash for
 *     anything it cannot parse rather than "Invalid Date" or today's date (§26).
 * ============================================================================
 *
 * Reached only with the Publisher plan — the gate is the route's, see src/router.tsx.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, Check, ChevronDown, Loader2, Pencil, Radio, Trash2 } from 'lucide-react';
import { BackLink, Btn, SectionHeading } from '../components/ui';
import {
  IssueList,
  PublisherOutletGlyphs,
  PublisherPlatformDrawer,
  ReleaseArtwork,
  ReleaseStatusBadge,
  ReleaseSummary,
} from '../components/publisher';
import { publisherApi } from '../api/publisherApi';
import { describeDeliveryClaim, describeQueueStatus } from '../api/labelgrid';
import { usePublisher, useSongs } from '../hooks';
import {
  formatReleaseDate,
  formatTimestamp,
  languageLabel,
  outletCountLabel,
  trackCountLabel,
} from '../lib/publisherFormat';
import { c } from '../styles/theme';
import type {
  PublisherRelease,
  PublisherReleasePlatform,
  PublisherReleasePlatformCatalogEntry,
  PublisherReleasePlatformStats,
  Song,
} from '../types/domain';
import { paths } from './paths';
import { useUiStore } from '../stores/uiStore';

function outletTitleFromKey(outletKey: string): string {
  return outletKey
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function asCatalogEntry(release: PublisherRelease, platform: PublisherReleasePlatform): PublisherReleasePlatformCatalogEntry {
  return {
    releaseId: release.id,
    outletKey: platform.outletKey,
    outletName: platform.outletName,
    outletType: null,
    accountAvailable: false,
    selected: release.outletKeys.includes(platform.outletKey),
    hasReportedStatus: true,
    status: platform.status,
    updatedAt: platform.updatedAt ?? null,
    isAiDsp: false,
    isUgcStore: false,
    statsAvailable: release.externalId != null,
  };
}

function unavailableStatsFor(entry: PublisherReleasePlatformCatalogEntry): PublisherReleasePlatformStats {
  return {
    releaseId: entry.releaseId,
    outletKey: entry.outletKey,
    outletName: entry.outletName,
    source: 'unavailable',
    availability: 'unavailable',
    reportedAt: entry.updatedAt ?? null,
    metrics: [
      {
        key: 'gross-reported',
        label: 'Gross reported',
        value: 'Not reported',
        reported: false,
        kind: 'money',
        note: 'No provider-backed platform amount is available for this outlet yet.',
      },
    ],
    rawProviderFields: [],
  };
}

export default function PublisherRelease() {
  const { releaseId } = useParams<{ releaseId: string }>();
  const navigate = useNavigate();
  const { songs } = useSongs();
  const setPublisherDeleteTarget = useUiStore((s) => s.setPublisherDeleteTarget);
  const {
    releases,
    submit,
    submitting,
    platforms: allPlatforms,
    eventsFor,
    issuesFor,
    setLastVisibleSurface,
  } = usePublisher();

  const release = releases.find((r) => r.id === releaseId) ?? null;

  const storedPlatforms = useMemo(
    () => (release ? allPlatforms.filter((platform) => platform.releaseId === release.id) : []),
    [allPlatforms, release]
  );
  const storedCatalog = useMemo(
    () => (release ? storedPlatforms.map((platform) => asCatalogEntry(release, platform)) : []),
    [release, storedPlatforms]
  );
  const [platforms, setPlatforms] = useState<PublisherReleasePlatform[]>(storedPlatforms);
  const [platformCatalog, setPlatformCatalog] = useState<PublisherReleasePlatformCatalogEntry[]>(storedCatalog);
  const [loadingPlatforms, setLoadingPlatforms] = useState(release ? storedPlatforms.length === 0 : true);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [activePlatformKey, setActivePlatformKey] = useState<string | null>(null);
  const [platformStatsByKey, setPlatformStatsByKey] = useState<Record<string, PublisherReleasePlatformStats>>({});
  const [loadingPlatformKey, setLoadingPlatformKey] = useState<string | null>(null);

  useEffect(() => {
    if (!release) return;
    setLastVisibleSurface({ kind: 'detail', releaseId: release.id });
  }, [release, setLastVisibleSurface]);

  /*
    Per-outlet delivery state, read through the API boundary when possible, but seeded
    demo/provider-shaped rows already stored on the release still count as explicit data.

    That prevents a mock live release from collapsing back to "No delivery status to
    report" only because the mock API cannot fetch fresh provider rows. In backend mode the
    network result still wins when it returns anything.
  */
  useEffect(() => {
    if (!releaseId) return;
    let live = true;
    setPlatforms(storedPlatforms);
    setPlatformCatalog(storedCatalog);
    setLoadingPlatforms(storedCatalog.length === 0);
    setActivePlatformKey(null);
    setPlatformStatsByKey({});
    setLoadingPlatformKey(null);
    void Promise.all([
      publisherApi.getReleasePlatforms(releaseId).catch(() => storedPlatforms),
      publisherApi.getReleasePlatformCatalog(releaseId).catch(() => storedCatalog),
    ])
      .then(([platformList, catalog]) => {
        if (!live) return;
        setPlatforms(platformList.length > 0 ? platformList : storedPlatforms);
        setPlatformCatalog(catalog.length > 0 ? catalog : storedCatalog);
      })
      .finally(() => {
        if (live) setLoadingPlatforms(false);
      });
    return () => {
      live = false;
    };
  }, [releaseId, storedCatalog, storedPlatforms]);

  const releaseSongs = useMemo<Song[]>(
    () =>
      release
        ? release.songIds
            .map((id) => songs.find((s) => s.id === id))
            .filter((s): s is Song => Boolean(s))
        : [],
    [release, songs]
  );

  if (!release) {
    return (
      <div>
        <BackLink fallbackTo={paths.publisherDashboard} fallbackLabel="Publisher" mode="parent" />
        <div
          style={{
            border: `1px solid ${c.border}`,
            borderRadius: 12,
            background: c.surface1,
            padding: 18,
          }}
        >
          <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 600, color: c.text1 }}>
            That release could not be found
          </p>
          <p style={{ margin: '0 0 14px', fontSize: 12, color: c.text2, lineHeight: 1.55 }}>
            It may have been deleted, or this account may not have access to it anymore.
          </p>
          <Btn onClick={() => navigate(paths.publisherDashboard)}>Back to the Publisher</Btn>
        </div>
      </div>
    );
  }

  const title = release.title.trim() || 'Untitled release';
  const issues = issuesFor(release);
  const events = eventsFor(release.id);
  /** Drafts and failed submissions remain editable; queued releases do not. */
  const editable =
    release.externalId == null && (release.status === 'draft' || release.status === 'failed');

  /**
   * Retries a submission.
   *
   * The store-level submission claim prevents a double click from producing two attempts.
   */
  async function handleSubmit() {
    if (!release) return;
    setSubmitError(null);
    const result = await submit(release.id);
    if (!result.ok) setSubmitError(result.message);
  }

  async function openPlatform(entry: PublisherReleasePlatformCatalogEntry) {
    if (!release) return;
    setActivePlatformKey(entry.outletKey);
    if (platformStatsByKey[entry.outletKey]) return;
    setLoadingPlatformKey(entry.outletKey);
    try {
      const stats = await publisherApi.getReleasePlatformStats(release.id, entry.outletKey);
      setPlatformStatsByKey((current) => ({ ...current, [entry.outletKey]: stats }));
    } catch {
      setPlatformStatsByKey((current) => ({
        ...current,
        [entry.outletKey]: unavailableStatsFor(entry),
      }));
    } finally {
      setLoadingPlatformKey((current) => (current === entry.outletKey ? null : current));
    }
  }

  const activePlatform =
    activePlatformKey == null
      ? null
      : platformCatalog.find((entry) => entry.outletKey === activePlatformKey) ?? {
          releaseId: release.id,
          outletKey: activePlatformKey,
          outletName: outletTitleFromKey(activePlatformKey),
          outletType: null,
          accountAvailable: false,
          selected: false,
          hasReportedStatus: false,
          status: null,
          updatedAt: null,
          isAiDsp: false,
          isUgcStore: false,
          statsAvailable: release.externalId != null,
        };

  const activePlatformStats = activePlatform ? platformStatsByKey[activePlatform.outletKey] ?? null : null;

  return (
    <div>
      <BackLink fallbackTo={paths.publisherDashboard} fallbackLabel="Publisher" mode="parent" />

      {/* ---- the release, at a glance ---- */}
      <div className="lyx-publisher-head">
        <div
          className="lyx-publisher-head-text"
          style={{ display: 'flex', alignItems: 'center', gap: 14 }}
        >
          <ReleaseArtwork
            release={release}
            songs={songs}
            height={64}
            radius={10}
            style={{ width: 64, flexShrink: 0, overflow: 'hidden' }}
          />
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
                margin: '0 0 4px',
              }}
            >
              <h1
                style={{
                  fontFamily: 'Space Grotesk',
                  fontWeight: 600,
                  fontSize: 16,
                  margin: 0,
                  color: release.title.trim() ? c.text1 : c.text2,
                }}
              >
                {title}
              </h1>
              <ReleaseStatusBadge status={release.status} />
            </div>
            <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.5 }}>
              {release.artistName.trim() ? `${release.artistName.trim()} · ` : ''}
              {release.releaseType} · {trackCountLabel(release.songIds.length)} · Updated{' '}
              {formatTimestamp(release.updatedAt)}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
          {editable && (
            // Back into the wizard with the release in hand, rather than a second editing
            // surface. §15's Edit action.
            <Btn
              variant="secondary"
              onClick={() => navigate(`${paths.publisherNewRelease}?release=${release.id}`)}
            >
              <Pencil size={13} />
              Edit
            </Btn>
          )}
          <Btn
            variant="secondary"
            onClick={() => setPublisherDeleteTarget(release, 'detail')}
            aria-label={`Delete ${title}`}
          >
            <Trash2 size={13} color={c.danger} />
            Delete
          </Btn>
        </div>
      </div>

      {/* ---- §26 — why it failed, when it did ----
          First, above everything else: a release in this state has one question attached
          to it, and the answer is the real reason recorded on the record. */}
      {release.status === 'failed' && (
        <section
          aria-label="Failure"
          style={{
            border: `1px solid ${c.danger}`,
            background: c.dangerSoft,
            borderRadius: 12,
            padding: '14px 16px',
            marginBottom: 18,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 9,
          }}
        >
          <AlertTriangle size={15} color={c.danger} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: '0 0 5px', fontSize: 12.5, fontWeight: 600, color: c.text1 }}>
              This release needs attention
            </p>
            <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>
              {/* The recorded reason, never a generic sentence: a failure with no
                  explanation is the thing §26 is about. */}
              {release.failureReason ??
                'No reason was recorded. Retry the submission, and if it fails again the event ' +
                  'history below carries what was attempted.'}
            </p>
          </div>
        </section>
      )}

      {/* ---- §22 — the metadata, as stored ---- */}
      <section aria-label="Release details" style={{ marginBottom: 22 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: detailsOpen ? 10 : 0,
          }}
        >
          <SectionHeading as="h2" size="sub" title="RELEASE DETAILS" marginBottom={0} />
          <Btn
            variant="secondary"
            onClick={() => setDetailsOpen((open) => !open)}
            aria-expanded={detailsOpen}
            aria-controls="publisher-release-details-panel"
          >
            <ChevronDown
              size={13}
              style={{
                transform: detailsOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 160ms ease',
              }}
            />
            {detailsOpen ? 'Hide details' : 'Show details'}
          </Btn>
        </div>

        {detailsOpen && (
          <div
            id="publisher-release-details-panel"
            style={{
              border: `1px solid ${c.border}`,
              background: c.surface1,
              borderRadius: 12,
              padding: 16,
            }}
          >
            <ReleaseSummary
              rows={[
                { label: 'Type', value: release.releaseType },
                { label: 'Artist', value: release.artistName.trim() },
                {
                  label: 'Tracks',
                  value:
                    releaseSongs.length > 0
                      ? releaseSongs.map((s) => s.title).join(', ')
                      : trackCountLabel(0),
                },
                { label: 'Genre', value: release.genre },
                { label: 'Language', value: languageLabel(release.language) },
                { label: 'Release date', value: formatReleaseDate(release.releaseDate) },
                { label: 'Explicit', value: release.explicit ? 'Yes' : 'No' },
                { label: 'Outlets', value: `${outletCountLabel(release.outletKeys.length)} selected` },
                { label: 'Created', value: formatTimestamp(release.createdAt) },
                /*
                  THE PROVIDER FIELDS. Each is null on every release in this build, and each
                  renders as a statement about who assigns it rather than as a value.

                  §24: no fake ISRC or UPC. §22: no invented provider status. `externalId`
                  being null is itself the proof that no release exists at LabelGrid — a
                  number here would mean a real API call succeeded.
                */
                {
                  label: 'Distributor ID',
                  value: release.externalId ?? 'Not created at the distributor yet',
                  mono: release.externalId != null,
                },
                {
                  label: 'UPC / barcode',
                  value: release.barcode ?? 'Assigned by the distributor',
                  mono: release.barcode != null,
                },
                {
                  label: 'ISRC',
                  // Per-track and issued by the provider. No release in this build has one,
                  // and computing one locally would be exactly §24's fake identifier.
                  value: 'Assigned per track by the distributor',
                },
                {
                  label: 'Provider review status',
                  value: release.providerReviewStatus ?? 'None reported',
                  mono: release.providerReviewStatus != null,
                },
                {
                  label: 'Provider delivery status',
                  value: release.providerDeliveryStatus ?? 'None reported',
                  mono: release.providerDeliveryStatus != null,
                },
              ]}
            />
          </div>
        )}
      </section>

      {/* ---- §23 — per-platform delivery ---- */}
      <SectionHeading as="h2" size="sub" title="PLATFORMS" marginBottom={10} />
      <div style={{ marginBottom: 22 }}>
        {loadingPlatforms ? (
          <p
            style={{
              margin: 0,
              fontSize: 12,
              color: c.text2,
              display: 'flex',
              alignItems: 'center',
              gap: 7,
            }}
          >
            <Loader2 size={13} className="lyx-spin" />
            Reading platform catalog…
          </p>
        ) : platformCatalog.length === 0 ? (
          <div
            style={{
              border: `1px solid ${c.border}`,
              background: c.surface1,
              borderRadius: 12,
              padding: '14px 16px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 9,
            }}
          >
            <Radio size={15} color={c.text2} style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: '0 0 6px', fontSize: 12.5, fontWeight: 500, color: c.text1 }}>
                No platform catalog to report
              </p>
              <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>
                The account outlet catalog was not available, and this release has no stored provider rows yet.
              </p>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {platformCatalog.map((entry) => {
              const platform =
                platforms.find((candidate) => candidate.outletKey === entry.outletKey) ??
                (entry.status
                  ? {
                      releaseId: release.id,
                      outletKey: entry.outletKey,
                      outletName: entry.outletName,
                      status: entry.status,
                      updatedAt: entry.updatedAt,
                    }
                  : null);
              const kind = describeQueueStatus(platform?.status ?? '');
              return (
                <button
                  key={entry.outletKey}
                  type="button"
                  className="lyx-platform-row"
                  onClick={() => void openPlatform(entry)}
                  aria-label={`Open ${entry.outletName} platform details`}
                  style={{
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <p
                      style={{
                        margin: '0 0 2px',
                        fontSize: 12.5,
                        color: c.text1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <PublisherOutletGlyphs
                        platforms={[
                          platform ?? {
                            releaseId: release.id,
                            outletKey: entry.outletKey,
                            outletName: entry.outletName,
                            status: entry.status ?? '',
                            updatedAt: entry.updatedAt,
                          },
                        ]}
                        size={14}
                        ariaLabel={entry.outletName}
                      />
                      <span>{entry.outletName}</span>
                    </p>
                    <p style={{ margin: '0 0 2px', fontSize: 11, color: c.text2, lineHeight: 1.5 }}>
                      {platform
                        ? describeDeliveryClaim(platform.status)
                        : 'No release-specific platform status has been reported.'}
                    </p>
                    <p style={{ margin: 0, fontSize: 10.5, color: c.text2 }}>
                      {entry.selected
                        ? 'Selected for this release'
                        : entry.accountAvailable
                          ? 'Available on this account'
                          : 'Stored provider row only'}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <p
                      style={{
                        margin: '0 0 2px',
                        fontFamily: 'IBM Plex Mono',
                        fontSize: 11,
                        color:
                          kind === 'failed' ? c.danger : kind === 'delivered' ? c.success : c.text2,
                      }}
                    >
                      {platform?.status || 'no status'}
                    </p>
                    <p style={{ margin: '0 0 2px', fontSize: 10.5, color: c.text2 }}>
                      {formatTimestamp(platform?.updatedAt ?? entry.updatedAt)}
                    </p>
                    <p style={{ margin: 0, fontSize: 10.5, color: c.text2 }}>
                      {entry.statsAvailable ? 'Open stats' : 'Open details'}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ---- submission, for a release that has not been sent ---- */}
      {editable && (
        <div style={{ marginBottom: 22 }}>
          <SectionHeading as="h2" size="sub" title="RELEASE" marginBottom={10} />
          {issues.some((i) => i.blocking) ? (
            <>
              <p style={{ margin: '0 0 10px', fontSize: 12, color: c.text2, lineHeight: 1.55 }}>
                This release is not ready to be submitted. What is outstanding:
              </p>
              <IssueList issues={issues} />
            </>
          ) : (
            <div>
              <Btn
                onClick={() => void handleSubmit()}
                disabled={submitting.includes(release.id)}
              >
                {submitting.includes(release.id) ? (
                  <Loader2 size={13} className="lyx-spin" />
                ) : (
                  <Check size={13} />
                )}
                Release
              </Btn>
              {submitError && (
                <p style={{ margin: '10px 0 0', fontSize: 11.5, color: c.danger, lineHeight: 1.55 }}>
                  {submitError}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ---- §26 — what actually happened, in order ---- */}
      <SectionHeading as="h2" size="sub" title="HISTORY" marginBottom={10} />
      {events.length === 0 ? (
        <p style={{ margin: 0, fontSize: 12, color: c.text2 }}>Nothing has happened yet.</p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {events.map((event) => (
            <li key={event.id} className="lyx-event-row">
              <span
                aria-hidden="true"
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  marginTop: 5,
                  flexShrink: 0,
                  background: event.source === 'provider' ? c.accentFill : c.borderStrong,
                }}
              />
              <div className="lyx-event-text">
                <p style={{ margin: 0, fontSize: 12, color: c.text1, lineHeight: 1.5 }}>
                  {event.message}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: c.text2 }}>
                  {/* The timestamp is whoever observed the event's own — the client clock
                      for a local action, the provider's for a provider one. Never
                      synthesised. §26. */}
                  {formatTimestamp(event.at)}
                  {event.source === 'provider' ? ' · reported by the distributor' : ''}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}

      {activePlatform ? (
        <PublisherPlatformDrawer
          entry={activePlatform}
          stats={activePlatformStats}
          loading={loadingPlatformKey === activePlatform.outletKey}
          onClose={() => setActivePlatformKey(null)}
        />
      ) : null}
    </div>
  );
}
